import { useFetch } from "@app/framework/hooks";
import { apiUrls, URLS } from "@app/shared";
import { Link } from "@design-system/components";
import { Pagination } from "@design-system/components/Pagination";
import { PaginationInfo } from "@design-system/types";
import { Loading } from "@modules/shared/components";
import { getCurrentLocaleCode } from "@mongez/localization";
import { useEffect, useState } from "react";
import { QuarterlyReport } from "../types";
import { YearsTabs } from "./YearsTabs";

type FinancialInformationResponse = {
  posts: QuarterlyReport[];
  years: number[];
  paginationInfo: PaginationInfo;
};

export function QuarterlyReportsTab() {
  const locale = getCurrentLocaleCode();
  const [filteredReports, setFilteredReports] = useState<QuarterlyReport[]>([]);

  const { data, load, isLoading } = useFetch<FinancialInformationResponse>(
    apiUrls.financialInformation.quarterlyReports,
  );

  useEffect(() => {
    load().then((data) => {
      setFilteredReports(data?.posts || []);
    });
  }, [locale]);

  const handleYearChange = (year: number | null) => {
    if (year === null) {
      setFilteredReports(data?.posts || []);
      return;
    }

    const filteredReports =
      data?.posts?.filter((record) => record.year === year) || [];
    setFilteredReports(filteredReports);
  };

  return (
    <div className="flex animate-fade flex-col gap-8">
      <div className="flex flex-col gap-6 rounded-3xl bg-white p-4 md:gap-8 md:p-8">
        <YearsTabs years={data?.years || []} onChange={handleYearChange} />
        {isLoading ? (
          <Loading />
        ) : (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-4">
              {filteredReports.map((record, index) => (
                <ReportItem key={index} record={record} />
              ))}
            </div>
            {data?.paginationInfo && (
              <Pagination
                onPageChange={(page) => {
                  load({ page });
                }}
                pushToHistory={false}
                paginationInfo={data.paginationInfo}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type ReportItemProps = {
  record: QuarterlyReport;
};

function ReportItem({ record }: ReportItemProps) {
  return (
    <Link
      to={URLS.investor.quarterlyReports.view(record.id)}
      title={record.title}
      className="group flex shrink-0 cursor-pointer flex-col gap-4 overflow-hidden rounded-3xl border border-[#E0E0E0] bg-white p-4 shadow-sm transition-colors duration-300 hover:bg-gray-100"
    >
      <p className="font-semibold text-marine group-hover:underline prose-table:border">
        {record.title}
      </p>
    </Link>
  );
}
